/* eslint-disable prettier/prettier */
import React, { Fragment, useState } from 'react';
import { useHistory } from 'react-router-dom';

import moment from 'moment';

import Header from 'components/AppBar';
import Loader from 'components/Loader';
import { Mobile } from 'components/Responsive';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { TravelReportObject } from 'models/allReservations';
import { AllReservationsReportRequest, AllReservationsReportResponse } from 'models/allReservationsReport';
import { RenderTravelReportViewAllReservations } from 'screens/components/AllReservationsReport/RenderTravelReportViewAllReservations';
import { getFormattedPrice } from 'utils/currency';
import { statusTranslation } from 'utils/statusTag';
import { Company } from 'models/company';
import { CostCenter } from 'models/costCenter';
import { BreakRulesReason } from 'services/travelBreakRulesReasonService';
import { Group } from 'models/group';
import { TravelRequested } from 'models/travelRequested';
import TravelRequestedDetails from 'screens/containers/TravelRequestedDetails';
import { AirFareConditions } from 'models/flightValidation';
import { FlightExchangeState } from 'models/flightExchange';
import { useDispatch, useSelector } from 'react-redux';
import { flightsExchangeParameters } from 'action-creators/flights';
import ConfirmationModal from 'components/ConfirmationModal';
import { RootState } from 'store';
import { cancelPendingTravel } from 'actions/travelRequested';
import { TextModal } from 'components/UserList/styles';
import { htmlT } from 'utils/generic';
import ResponseScreen, { BackgroundTypes } from '../ResponseScreen';
import { IconTypes } from '../BookingResponseScreen';
import { AdminUser } from 'models/adminUser';

interface Props {
  allReservationsList: TravelReportObject[];
  allCompaniesList: Company[];
  allUsersList: AdminUser[];
  allCostCenterList: CostCenter[];
  allBreakRuleReasonIdList: BreakRulesReason[];
  allUserGroupIdList: Group[];
  fetchReportSummary: (filters: AllReservationsReportRequest, type: string) => Promise<any>;
  fetchUsersAndInvites: (companyId: string) => Promise<void>;
  isLoading: boolean;
  isSubmit: boolean;
  report?: AllReservationsReportResponse;
}

const AllReservationsReport = ({ allReservationsList, allCompaniesList, allUsersList, allCostCenterList, allBreakRuleReasonIdList, allUserGroupIdList, fetchReportSummary, fetchUsersAndInvites,isLoading, isSubmit, report }: Props) => {
  const { t } = useStoreTranslation();
  const history = useHistory();
  const isMobile = useIsMobile();
  const handlePeriodText = (text?: string) => { };

  const navigateToDashboard = () => history.push('/corporate/booking');

  const formattedPrice = (price?: number, currency?: string): string => {
    const { formatted } = getFormattedPrice({ value: price || 0, currency: currency, t });
    return formatted;
  };

  const formatDate = (date: string) => moment(date).format('DD/MM/YYYY');

  const formatStatus = (status: string) => statusTranslation(status, t);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTravelReport, setSelectedTravelReport] = useState('');
  const [id, setId] = useState('');
  const [isShow, setIsShow] = useState(false);
  const [flight , setFlight] = useState<TravelRequested>();
  const [isMounted, setIsMounted] = useState(false);
  const dispatch = useDispatch();
  const [idForCancel, setIdForCancelTravelItineraryId] = useState('');
  const [isShowConfirmCancel, setIsShowConfirmCancel] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isShowResultConfirmCancel, setIsShowResultConfirmCancel] = useState(false);
  const [resultConfirmCancelText, setResultConfirmCancelText] = useState('');
  const [resultConfirmCancelSubText, setResultConfirmCancelSubText] = useState(
    t('travelRequested.confirmCancel.success.subtext'),
  );
  const userAccount = useSelector((state: RootState) => state.user?.accountResult);

  const handleRowClick = (id: string) => {
    setSelectedTravelReport(id);
    setShowDetails(true);
    setIsShow(true);
  };

  const handleCloseDetails = () => {
    setId('');
    setSelectedTravelReport('');
    setIsShow(false);
    setFlight(undefined);
  };

  const handleOnExchange = (element: TravelRequested, airFareConditions?: AirFareConditions[]) => {
    const flight: FlightExchangeState = {
      departureCode: element.departurePoint,
      arrivalCode: element.arrivalPoint,
      departureCity: element.flightDepartureCity,
      departureDate: new Date(element.date),
      arrivalCity: element.flightCity,
      arrivalDate: element.returnDate ? new Date(element.returnDate) : undefined,
      originalFlight: element,
      airFareConditions,
    };
    dispatch(flightsExchangeParameters(flight));
    history.push('/corporate/travelRequested/exchange-options');
  };

  const handleOnCancel = (element: TravelRequested) => {
    setIdForCancelTravelItineraryId(element.id);
    setIsShowConfirmCancel(true);
  };

  const onCloseConfirmCancel = () => {
    setIdForCancelTravelItineraryId('');
    setIsShowConfirmCancel(false);
  };


  const onConfirmCancel = async () => {
    try {
      setIsCancelling(true);
      const {
        data: { errors, succeeded },
      } = await cancelPendingTravel(idForCancel, userAccount?.id ?? '', userAccount?.sessionToken ?? '');

      setIsCancelling(false);

      if (succeeded) {
        setIsShowConfirmCancel(false);

        setResultConfirmCancelText(t('travelRequested.confirmCancel.success.text'));
        setResultConfirmCancelSubText(t('travelRequested.confirmCancel.success.subtext'));
        setIsShowResultConfirmCancel(true);
      } else {
        if (errors?.indexOf('CannotCancel') !== -1) {
          setResultConfirmCancelText(t('travelRequested.confirmCancel.error.cannotCancel.text'));
          setResultConfirmCancelSubText(t('travelRequested.confirmCancel.error.cannotCancel.subtext'));
        } else {
          setResultConfirmCancelText(t('travelRequested.confirmCancel.error.genericText'));
          setResultConfirmCancelSubText(t('travelRequested.confirmCancel.error.genericSubText'));
        }

        setIsShowResultConfirmCancel(true);
      }

      setIsShowConfirmCancel(false);
      setIsShow(false);
      setIdForCancelTravelItineraryId('');
    } catch (error) {
      setResultConfirmCancelText(t('travelRequested.confirmCancel.error.genericText'));
      setResultConfirmCancelSubText(t('travelRequested.confirmCancel.error.genericSubText'));
      setIsShowResultConfirmCancel(true);
      setIsShowConfirmCancel(false);
    }
  };

  const onResultConfirmCancelClick = () => {
    onCloseConfirmCancel();
    setIsShowResultConfirmCancel(false);
    setIsShow(false);
    setIsCancelling(false);
    setIdForCancelTravelItineraryId('');

    history.push('/corporate/booking');
  };

  return (
    <Fragment>
      {isLoading ? (
        <Loader fullScreen />
      ) : (
        <Fragment>
          <Mobile>
            <Header goBack={navigateToDashboard}>{t('allReservationsReport.report.title')}</Header>
          </Mobile>
          {RenderTravelReportViewAllReservations(
            true,
            allReservationsList,
            allCompaniesList,
            allUsersList,
            allCostCenterList,
            allBreakRuleReasonIdList,
            allUserGroupIdList,
            isLoading,
            isSubmit,
            isMobile,
            t,
            fetchReportSummary,
            fetchUsersAndInvites,
            handlePeriodText,
            formattedPrice,
            formatDate,
            formatStatus,
            report,
            handleRowClick,
            handleCloseDetails,
            showDetails,
            selectedTravelReport,
            isShow,
          )}
        </Fragment>
      )}
        <TravelRequestedDetails
          id={selectedTravelReport}
          show={isShow}
          onClose={handleCloseDetails}
          onChange={(airFareConditions?: AirFareConditions[]) => {
            if (flight) handleOnExchange(flight, airFareConditions);
          }}
          onCancel={() => {
            if (flight) handleOnCancel(flight);
          }}
        />
        <ConfirmationModal
        open={isShowConfirmCancel}
        title={t('travelRequested.confirmCancel.title')}
        onClose={onCloseConfirmCancel}
        onConfirm={onConfirmCancel}
        submitting={isCancelling}
        showCancelButton={true}
        disabled={isCancelling}
      >
        <TextModal>{htmlT(t, 'travelRequested.confirmCancel.text')}</TextModal>
      </ConfirmationModal>

      <ResponseScreen
        show={isShowResultConfirmCancel}
        text={resultConfirmCancelText}
        subText={resultConfirmCancelSubText}
        buttonText={t('travelRequested.confirmCancel.error.buttonText')}
        background={BackgroundTypes.Cloud}
        icon={IconTypes.Airplane}
        buttonOnClick={onResultConfirmCancelClick}
      />

    </Fragment>
  );
};

export default AllReservationsReport;
